/**
 * D52 — the user's notice for one dropped session credential: short, plain, naming the credential and why. Pure (a
 * `t` from the `agent` namespace is passed in), so it is unit-tested without React.
 */
import { isReservedCredentialEnvName, isUsableEnvName, SANDBOX_ENV_NAME_RE, usableEnvName } from '@papan/sandbox/env-names'
import type { CredentialDropNotice } from '../core/credential-drop-notice.js'

type T = (key: string, opts?: Record<string, unknown>) => string

const KEY_BY_REASON: Record<string, string> = {
  'access-revoked': 'credentialDropped.accessRevoked',
  'entry-deleted': 'credentialDropped.entryDeleted',
  'no-entry-id': 'credentialDropped.notSaved',
  'decrypt-failed': 'credentialDropped.decryptFailed',
  'value-missing': 'credentialDropped.valueMissing',
}

export function describeCredentialDrop(t: T, d: CredentialDropNotice): string {
  if (d.reason === 'invalid-env-name') {
    // the name IS the problem: name the bad one and offer the usable spelling the picker would make of it
    const bad = d.envVars.filter(v => !isUsableEnvName(v))
    const named = bad.length > 0 ? bad : d.envVars
    const first = named[0] ?? d.label ?? ''
    // #1500: a well-formed name the rule reserves (PATH, BASH_ENV, LD_PRELOAD…) — say it is reserved, not "invalid"
    const reserved = bad.length > 0 && bad.every(v => SANDBOX_ENV_NAME_RE.test(v) && isReservedCredentialEnvName(v))
    return t(reserved ? 'credentialDropped.reservedEnvName' : 'credentialDropped.invalidEnvName', { name: named.join(', ') || first, suggestion: usableEnvName(first) })
  }
  const name = d.label || d.envVars.join(', ')
  return t(KEY_BY_REASON[d.reason] ?? 'credentialDropped.other', { name })
}
